import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { Loading, SeverityBadge } from "../../components/ui";
import { t } from "../../i18n/t";
import { formatDate, formatNumber } from "../../lib/format";
import {
  kommuneExposure,
  kommuneSummaries,
  schoolsForKommune,
  STATUS_ORDER,
  type NationalSchool,
  type SchoolStatus,
} from "../../lib/national";
import { routes } from "../../lib/routes";
import { useNational } from "../../lib/useNational";
import { usePageTitle } from "../../lib/usePageTitle";
import { LoadError } from "./LoadError";

function PublicStatusBadge({ status }: { status: SchoolStatus }) {
  return (
    <span className={`badge public-status public-status-${status}`}>
      {t(`publicStatus.${status}`)}
    </span>
  );
}

function SchoolCard({ school }: { school: NationalSchool }) {
  const headingId = `school-${school.school_id}`;
  return (
    <li>
      <article className="card school-card" aria-labelledby={headingId}>
        <header className="school-card-head">
          <h3 id={headingId}>{school.name}</h3>
          <PublicStatusBadge status={school.status} />
        </header>
        {school.trinn && (
          <p className="muted small">{t("kommunePublic.trinn", { trinn: school.trinn })}</p>
        )}
        <p>{school.reason}</p>
        {school.exposure && school.exposure.addresses > 0 && (
          <p className="small">
            {t("kommunePublic.schoolExposure", {
              withHigh: formatNumber(school.exposure.with_high),
              addresses: formatNumber(school.exposure.addresses),
              pct: formatNumber(
                Math.round((100 * school.exposure.with_high) / school.exposure.addresses),
              ),
            })}
          </p>
        )}
        {school.top_findings.length === 0 ? (
          <p className="muted">{t("kommunePublic.noFindings")}</p>
        ) : (
          <>
            <h4>{t("kommunePublic.topFindings")}</h4>
            <ul className="finding-list">
              {school.top_findings.map((f, i) => (
                <li key={`${f.title}-${f.location}-${i}`}>
                  <p className="finding-title">
                    {f.severity && <SeverityBadge severity={f.severity} />} {f.title}
                  </p>
                  {f.location && <p className="muted small">{f.location}</p>}
                  <p className="small">
                    {t("kommunePublic.exposure", {
                      exposure: formatNumber(f.exposure),
                      addresses: formatNumber(school.addresses),
                    })}
                  </p>
                  {f.omgivelse === "faa_boliger" && (
                    <p className="muted small">
                      {t("kommunePublic.faaBoliger")}. {t("kommunePublic.faaBoligerHint")}
                    </p>
                  )}
                  {f.tiltak && (
                    <p className="small">{t("kommunePublic.tiltak", { tiltak: f.tiltak })}</p>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </article>
    </li>
  );
}

/** `/kommuner/:nr`: preliminary results for every school in one kommune, with a call to action. */
export function KommunePublicPage() {
  const { nr = "" } = useParams();
  const national = useNational();
  const view = useMemo(() => {
    if (!national.data) return null;
    const run = national.data.runs.find((r) => r.kommune === nr) ?? null;
    const summary = run ? (kommuneSummaries(national.data).find((k) => k.nr === nr) ?? null) : null;
    return { run, summary, schools: run ? schoolsForKommune(national.data, nr) : [] };
  }, [national.data, nr]);
  const name = view?.run?.name;
  usePageTitle(name ? t("kommunePublic.title", { kommune: name }) : t("kommunerPage.title"));

  if (national.loading) {
    return (
      <div className="page narrow">
        <h1>{t("kommunePublic.titleGeneric")}</h1>
        <Loading />
      </div>
    );
  }
  if (national.error || !national.data || !view) {
    return (
      <div className="page narrow">
        <h1>{t("kommunePublic.titleGeneric")}</h1>
        <LoadError message={t("kommunePublic.loadFailed")} onRetry={national.reload} />
      </div>
    );
  }
  const { run, summary, schools } = view;
  if (!run || !summary) {
    return (
      <div className="page narrow">
        <h1>{t("kommunePublic.notFound", { nr })}</h1>
        <p>
          <Link to={routes.kommuner()}>{t("kommunePublic.backToList")}</Link>
        </p>
      </div>
    );
  }

  const fetched = run.snapshot_at ?? national.data.generated_at;
  const counts = summary.counts;
  const totalExposure = kommuneExposure(schools);
  const ruleText = national.data.rule?.description;

  return (
    <div className="page narrow public-kommune">
      <header className="page-header">
        <h1>{t("kommunePublic.title", { kommune: run.name })}</h1>
        <p className="muted">
          {fetched
            ? t("kommunePublic.metaFetched", { nr, date: formatDate(fetched) })
            : t("kommunePublic.meta", { nr })}
        </p>
      </header>

      {schools.length === 0 ? (
        <p>{t("kommunePublic.noSchools")}</p>
      ) : (
        <section aria-label={t("kommunePublic.summaryLabel")}>
          <p className="lead">
            {schools.length === 1
              ? t("kommunePublic.summaryOne", counts)
              : t("kommunePublic.summaryMany", {
                  schools: formatNumber(schools.length),
                  ...counts,
                })}
          </p>
          {totalExposure && totalExposure.addresses > 0 && (
            <p>
              {t("kommunePublic.kommuneExposure", {
                withHigh: formatNumber(totalExposure.with_high),
                addresses: formatNumber(totalExposure.addresses),
              })}
            </p>
          )}
          <ul className="plain-list chip-row">
            {STATUS_ORDER.map((s) => (
              <li key={s}>
                <span className={`badge public-status public-status-${s}`}>
                  {t("kommunePublic.chip", { label: t(`publicStatus.${s}`), n: counts[s] })}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="cta-inline">
        <p>{t("kommunePublic.ctaTitle", { kommune: run.name })}</p>
        <Link to={routes.contact(nr)} className="btn btn-primary">
          {t("kommunePublic.ctaOrder")}
        </Link>
      </div>

      <section className="notice notice-info" aria-label={t("kommunePublic.noticeLabel")}>
        <p>{t("kommunePublic.disclaimer")}</p>
        {ruleText && (
          <details>
            <summary>{t("kommunePublic.howColour")}</summary>
            <p>{ruleText}</p>
          </details>
        )}
      </section>

      {schools.length > 0 && (
        <section>
          <h2>{t("kommunePublic.schoolsTitle")}</h2>
          <ul className="plain-list">
            {schools.map((s) => (
              <SchoolCard key={s.school_id} school={s} />
            ))}
          </ul>
        </section>
      )}

      {(run.warnings.length > 0 || run.attribution) && (
        <section className="data-basis">
          <h2>{t("kommunePublic.dataTitle")}</h2>
          {run.warnings.length > 0 && (
            <ul>
              {run.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}
          {run.attribution && <p className="muted small">{run.attribution}</p>}
        </section>
      )}

      <section className="card cta-card" aria-labelledby="kommune-cta">
        <h2 id="kommune-cta">{t("kommunePublic.ctaTitle", { kommune: run.name })}</h2>
        <ul>
          <li>{t("kommunePublic.ctaOne")}</li>
          <li>{t("kommunePublic.ctaTwo")}</li>
          <li>{t("kommunePublic.ctaThree")}</li>
          <li>{t("kommunePublic.ctaFour")}</li>
          <li>{t("kommunePublic.ctaFive")}</li>
        </ul>
        <p className="cta-actions">
          <Link to={routes.contact(nr)} className="btn btn-primary">
            {t("kommunePublic.ctaOrder")}
          </Link>
          <a href="/nasjonal/" className="btn btn-secondary">
            {t("kommunePublic.ctaMap")}
          </a>
        </p>
      </section>
    </div>
  );
}
